import {
  CAPTURE_VAULT_OBJECT_SCALE_WEIGHT_STEP,
  CaptureVaultObjectSizeConfig,
  RawFileData,
  VAULT_OBJECT_UPLOAD_DEFAULT_DATA,
  VaultObjectUploadData,
} from "@/components/vault/vault-object-util";
import {
  LoaderData,
  VaultObjectViewerData,
} from "@/components/vault/viewer/VaultObjectViewerModal";
import useGaiaStoreContainer from "@/engine/viewer/core/useGaiaStoreContainer";
import useVaultFilterStore from "@/stores/zustand/vault/useVaultFilterStore";
import {
  VaultData,
  VaultObjectAnnotationDto,
  VaultObjectData,
  VaultObjectPreviewDto,
} from "@/types/data-types";
import { VAULT_OBJECT_TYPE } from "@/types/enums";
import { FilterBackgroundName } from "@/types/vault-object-filter-types";
import { calculateTextColor } from "@/utils/commonUtil";
import FilterPreset from "@/utils/filterPreset";
import _ from "lodash";
import { create } from "zustand";
import { VAULT_DATA_SAMPLE, VAULT_OBJECT_SAMPLES } from "@/utils/mockUtil";
import { dataURLtoFile } from "@/utils/threeUtil";
import sanitizer from "sanitize-filename";

export type CurrentPageType = "viewer" | "uploader" | "uploader-thumbnail";

interface VaultStoreInterface {
  /* Database */
  database: { vaultObjectList: VaultObjectData[] };
  /* Vault */
  vault: VaultData;
  vaultFeed: VaultObjectPreviewDto[];
  /* VaultObject Viewer */
  isViewerOpen: boolean;
  canvasStatus: "idle" | "loading" | "render" | "error" | "hidden";
  hoverPoint: [number, number, number] | null;
  viewerSource: "Origin" | "Embed";
  currentPage: CurrentPageType;
  currentTab: "metadata" | "show-window" | "social";
  vaultObjectViewerLoaderData: LoaderData | null;
  vaultObjectViewerData: VaultObjectViewerData | null;
  vaultCreateModalOpen: boolean;
  uploadData: VaultObjectUploadData | null;
  captureVaultObjectSizeConfig: CaptureVaultObjectSizeConfig | null;
  vaultObjectUploadFileList: FileList | null;
  uploadCompleteVaultObject: VaultObjectData | null;
  deleteCompleteVaultObject: VaultObjectData | null;
  vaultObjectViewerTextColor: string;
  toggleInspector: boolean;

  vaultStoreActions: {
    /* Database */
    addVaultObject: (vaultObject: VaultObjectData) => void;
    getVaultObject: (uid: string) => VaultObjectData;
    /* VaultObject Viewer */
    setCanvasStatus: (
      canvasStatus: "idle" | "loading" | "render" | "error" | "hidden",
    ) => void;
    updateHoverPoint: (hoverPoint: [number, number, number] | null) => void;
    openVaultObjectViewer: (vaultObjectUid: string) => void;
    closeVaultObjectViewer: () => void;
    openVaultObjectUpload: (fileList: FileList) => void;
    setCurrentPage: (
      currentPage: "viewer" | "uploader" | "uploader-thumbnail",
    ) => void;
    setCurrentTab: (
      updateFn:
        | ("metadata" | "show-window" | "social")
        | ((
            currentTab: "metadata" | "show-window" | "social",
          ) => "metadata" | "show-window" | "social"),
    ) => void;
    openVaultCreateModalOpen: () => void;
    closeVaultCreateModalOpen: () => void;
    updatePreprocessedData: (preprocessedData: RawFileData | null) => void;
    updateUploadData: (uploadData: Partial<VaultObjectUploadData>) => void;
    updateUploadAnnotationsData: (
      updateFn: (
        prevAnnotations: VaultObjectAnnotationDto[],
      ) => VaultObjectAnnotationDto[],
    ) => void;
    initCaptureVaultObjectSizeConfig: (
      captureVaultObjectSizeConfig: CaptureVaultObjectSizeConfig,
    ) => void;
    nextSizeCaptureVaultObjectSizeConfig: () => void;
    uploadViewingVaultObject: (
      onStart: () => void,
      onEnd: () => void,
      onComplete: (result: VaultObjectData) => void,
      onError: () => void,
    ) => Promise<void>;
    updateVaultObjectViewerTextColor: (isLoading: boolean) => void;
    setToggleInspector: (toggleInspector: boolean) => void;
  };
}

const useVaultStore = create<VaultStoreInterface>((set, get) => ({
  /* Database */
  database: { vaultObjectList: VAULT_OBJECT_SAMPLES },
  /* Vault */
  vault: VAULT_DATA_SAMPLE,
  vaultFeed: VAULT_OBJECT_SAMPLES.map((item) => ({
    id: item.id,
    uid: item.uid,
    type: item.type,
    name: item.name,
    thumbnailVersion: item.thumbnailVersion,
    thumbnailUrl: item.thumbnailUrl,
    backgroundHex:
      item.filter?.background.type === FilterBackgroundName.Color
        ? item.filter.background.hex
        : undefined,
  })),
  /* VaultObject Viewer */
  isViewerOpen: false,
  canvasStatus: "idle",
  hoverPoint: null,
  viewerSource: "Origin",
  currentPage: "viewer",
  currentTab: "metadata",
  vaultObjectViewerLoaderData: null,
  vaultObjectViewerData: null,
  splatScene: null,
  splatObject: null,
  vaultCreateModalOpen: false,
  vaultEditModalOpen: false,
  uploadData: null,
  editData: null,
  captureVaultObjectSizeConfig: null,
  vaultObjectUploadFileList: null,
  uploadCompleteVaultObject: null,
  deleteCompleteVaultObject: null,
  vaultObjectViewerTextColor: "#000000",
  toggleInspector: false,

  vaultStoreActions: {
    /* Database */
    addVaultObject: (vaultObject: VaultObjectData) => {
      set((state) => ({
        database: {
          vaultObjectList: [vaultObject, ...state.database.vaultObjectList],
        },
      }));
    },
    getVaultObject: (uid: string) => {
      const findVaultObject = get().database.vaultObjectList.find(
        (item) => item.uid === uid,
      );
      if (!findVaultObject) throw new Error();

      return findVaultObject;
    },
    /* VaultObject Viewer */
    setCanvasStatus: (canvasStatus) => {
      set({
        canvasStatus,
      });
    },
    updateHoverPoint: (hoverPoint) => {
      set({ hoverPoint });
    },
    openVaultObjectViewer: (vaultObjectUid) => {
      const data = get().vaultStoreActions.getVaultObject(vaultObjectUid);

      set({
        isViewerOpen: true,
        currentPage: "viewer",
        currentTab: "metadata",
        toggleInspector: false,
        vaultObjectViewerLoaderData:
          data.type === VAULT_OBJECT_TYPE.OBJECT && data.assetData
            ? {
                name: data.name,
                extension: data.assetData.extension,
                url: data.assetData.signedUrl,
              }
            : null,
        vaultObjectViewerData: data,
      });

      useGaiaStoreContainer
        .getState()
        .gaiaStoreContainerActions.changeViewingHengeLayers(null);
      useGaiaStoreContainer
        .getState()
        .gaiaStoreContainerActions.changeSelectedLayer(null);

      useVaultFilterStore
        .getState()
        .actions.initiateFilterPreset(
          data.filter ?? FilterPreset.Default_241217,
        );
    },
    closeVaultObjectViewer: () => {
      function reset() {
        set({
          isViewerOpen: false,
          currentPage: "viewer",
          vaultObjectViewerLoaderData: null,
          vaultObjectViewerData: null,
          vaultObjectUploadFileList: null,
        });
      }

      const currentPage = get().currentPage;

      switch (currentPage) {
        case "viewer":
        case "uploader":
        case "uploader-thumbnail": {
          const loaderData = get().vaultObjectViewerLoaderData;
          if (loaderData) {
            URL.revokeObjectURL(loaderData.url);
          }

          reset();
          break;
        }
      }
    },
    openVaultObjectUpload: (fileList) => {
      set({
        isViewerOpen: true,
        currentPage: "uploader",
        currentTab: "metadata",
        vaultObjectUploadFileList: fileList,
        uploadData: null,
      });

      useGaiaStoreContainer
        .getState()
        .gaiaStoreContainerActions.changeViewingHengeLayers(null);
      useGaiaStoreContainer
        .getState()
        .gaiaStoreContainerActions.changeSelectedLayer(null);

      useVaultFilterStore
        .getState()
        .actions.initiateFilterPreset(FilterPreset.Default_241217);
    },
    setCurrentPage: (currentPage) => {
      set({ currentPage });
    },
    setCurrentTab: (updateFn) => {
      set((state) => ({
        currentTab:
          typeof updateFn === "function"
            ? updateFn(state.currentTab)
            : updateFn,
      }));
    },
    openVaultCreateModalOpen: () => set({ vaultCreateModalOpen: true }),
    closeVaultCreateModalOpen: () => set({ vaultCreateModalOpen: false }),
    updatePreprocessedData: (preprocessedData) => {
      const loaderData = get().vaultObjectViewerLoaderData;
      if (loaderData) {
        URL.revokeObjectURL(loaderData.url);
      }

      set({
        vaultObjectViewerLoaderData: preprocessedData
          ? {
              name: preprocessedData.name,
              extension: preprocessedData.extension,
              url: preprocessedData.url,
            }
          : null,
        uploadData: preprocessedData
          ? {
              ...preprocessedData,
              ...VAULT_OBJECT_UPLOAD_DEFAULT_DATA,
            }
          : null,
        captureVaultObjectSizeConfig: null,
      });
    },
    updateUploadData: (uploadData) => {
      set((state) => ({
        uploadData: _.assign(state.uploadData, uploadData),
      }));
    },
    updateUploadAnnotationsData: (updateFn) => {
      set((state) => ({
        uploadData: state.uploadData
          ? {
              ...state.uploadData,
              annotations: updateFn(state.uploadData.annotations),
            }
          : null,
      }));
    },
    initCaptureVaultObjectSizeConfig: (captureVaultObjectSizeConfig) => {
      set({
        captureVaultObjectSizeConfig,
      });
    },
    nextSizeCaptureVaultObjectSizeConfig: () => {
      const config = get().captureVaultObjectSizeConfig;
      if (!config) return;

      const nextSizeWeight =
        (config.currentSizeWeight + 1) % (config.maxSizeWeight + 1);

      if (get().currentPage === "uploader-thumbnail") {
        get().vaultStoreActions.updateUploadData({
          thumbnailScale: Math.min(
            config.maxSize,
            1 + nextSizeWeight * CAPTURE_VAULT_OBJECT_SCALE_WEIGHT_STEP,
          ),
        });
      }

      set((state) => ({
        captureVaultObjectSizeConfig: state.captureVaultObjectSizeConfig
          ? {
              ...state.captureVaultObjectSizeConfig,
              currentSizeWeight: nextSizeWeight,
            }
          : null,
      }));
    },
    uploadViewingVaultObject: async (onStart, onEnd, onComplete, onError) => {
      const uploadData = get().uploadData;
      if (!uploadData) {
        onError();
        return;
      }

      if (uploadData.name === "") {
        set({
          currentPage: "uploader",
          currentTab: "metadata",
        });
        onError();
        return;
      }

      if (uploadData.tags.length === 0) {
        set({
          currentPage: "uploader",
          currentTab: "metadata",
        });
        onError();
        return;
      }

      let backgroundFile: File | undefined;
      if (uploadData.filter.background.type === FilterBackgroundName.Image) {
        if (!uploadData.filter.background.file) {
          onError();
          return;
        }

        backgroundFile = uploadData.filter.background.file;
      }

      try {
        // Thumbnail image
        const thumbnailCanvas = document.querySelector(
          "#vault-object-thumbnail-hidden-viewer",
        ) as HTMLCanvasElement;

        if (!thumbnailCanvas) {
          onError();
          return;
        }

        const imageData = thumbnailCanvas.toDataURL("image/png");
        const thumbnailImage = dataURLtoFile(
          imageData,
          sanitizer(`${uploadData.name}_preview.png`),
        );

        const mainFile = uploadData.file;
        const additionalFiles: File[] = [];

        // const request: CreateVaultObjectRequest = {
        //   vaultUid: vaultData.uid,
        //   type: VAULT_OBJECT_TYPE.OBJECT,
        //   layers: null,
        //   ...uploadData,
        // };

        onStart();
        // const res = await fetchCreateVaultObjectByUploadAPI(
        //   request,
        //   { mainFile, additionalFiles },
        //   thumbnailImage,
        //   backgroundFile,
        // );

        // if (res.code === ApiResult.OK.code) {
        // const uploadedObject = res.data;
        // const preview: VaultObjectPreviewDto = {
        //   id: uploadedObject.id,
        //   uid: uploadedObject.uid,
        //   type: uploadedObject.type,
        //   name: uploadedObject.name,
        //   thumbnailUrl: uploadedObject.thumbnailUrl,
        //   thumbnailVersion: uploadedObject.thumbnailVersion,
        //   backgroundHex:
        //     uploadedObject.filter?.background.type ===
        //     FilterBackgroundName.Color
        //       ? uploadedObject.filter.background.hex
        //       : undefined,
        // };

        // set((state) => ({
        //   viewingFeedPageData: state.viewingFeedPageData
        //     ? {
        //       items: [preview, ...state.viewingFeedPageData.items],
        //       nextCursor: state.viewingFeedPageData.nextCursor,
        //     }
        //     : null,
        // }));
        //
        //   onComplete(uploadedObject);
        // } else {
        //   onError();
        // }
      } catch {
        onError();
      } finally {
        onEnd();
      }
    },
    updateVaultObjectViewerTextColor: (isLoading) => {
      let textColor = "#000000";
      const currentFilter = useVaultFilterStore.getState().currentFilter;
      if (isLoading) {
        textColor = "#000000";
      } else if (currentFilter.background.type === FilterBackgroundName.Color) {
        textColor = calculateTextColor(currentFilter.background.hex);
      }

      set({ vaultObjectViewerTextColor: textColor });
    },
    setToggleInspector: (toggleInspector: boolean) => set({ toggleInspector }),
  },
}));

export default useVaultStore;
export const useVaultStoreActions = () =>
  useVaultStore((state) => state.vaultStoreActions);
